import ReactDOMServer from 'react-dom/server'
import 'ag-grid-enterprise'
import { useSelector } from 'react-redux'
import { selectIdeaListInput } from '../../../../redux/slices/idea-list-input.slice.ts'
import { IdeyaAgGridTable } from '../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ColDef, GridApi } from 'ag-grid-community'
import {
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  IGetRowsParams,
  MenuItemDef,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-enterprise'
import { getBaseQuery, getBaseUrl } from '../../../../apis/api.util.ts'
import {
  IdeaListItem,
  IdeaListRequest,
  IdeaListResponse,
} from '../../../../apis/idea/idea-api.def.ts'
import type { BaseQueryApi } from '@reduxjs/toolkit/query'
import {
  coloredNumberCellStyle,
  currencyValueFormatter,
  marketDateTimezoneFormatter,
  percentageValueFormatter,
} from '../../../../utils/formatters.ts'
import { sortMapping } from './idea-list-table.def.ts'
import { EditOutlined, EyeOutlined } from '@ant-design/icons'
import LiquidateIcon from '../../../../assets/icons/idea-list/liquidate.svg'
import LiquidateDarkIcon from '../../../../assets/icons/idea-list/liquidate-dark.svg'
import { useAppSelector } from '../../../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '../../../../features/setting/setting.slice.ts'
import { Theme } from '../../../../features/setting/setting.def.ts'
import { IDEA_LIST_COLUMN_STATE_KEY } from '../../../../utils/constants.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeaActionColumn } from './idea-action-column.tsx'
import { useIsMobile } from '../../../../common/hooks/use-is-mobile.ts'
import { IdeaDirectionTag } from './idea-direction-tag.tsx'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { IdeaTotalPnlCell } from './idea-total-pnl-cell.tsx'
import dayjs from 'dayjs'
import { getCurrentQuarterRange } from '../../../../utils/time.ts'
import { IsTrialCellRenderer } from '../../../components/is-trial-cell-renderer.tsx'

function buildIdeaListUrl(request: IdeaListRequest) {
  const params = new URLSearchParams({
    page: String(request.page),
    pageSize: String(request.pageSize ?? ''),
    includeLiquidated: String(request.includeLiquidated),
    firmId: request.firmId ?? '',
    userId: request.userId ?? '',
    region: request.region ?? '',
    netCategory: request.netCategory ?? '',
    orderBy: request.orderBy ?? '',
    orderDirection: request.orderDirection ?? '',
    filterColumn: request.filterColumn ?? '',
    filterText: request.filterText ?? '',
    startDate: request.startDate ?? '',
    endDate: request.endDate ?? '',
  })
  return `/list?${params.toString()}`
}

export interface IdeaListTableRef {
  refresh: () => void
}

interface IdeaListTableProps {
  onRefreshed?: () => void
  onEditIdea: (id: string) => void
  onViewIdea: (id: string) => void
  onViewHistoryIdea: (id: string) => void
  onLiquidateIdea: (id: string) => void
  height: number
}

export const IdeaListTable = forwardRef<IdeaListTableRef, IdeaListTableProps>(
  (
    {
      onRefreshed,
      onEditIdea,
      onViewIdea,
      onViewHistoryIdea,
      onLiquidateIdea,
      height,
    },
    ref
  ) => {
    const ideaListInput = useSelector(selectIdeaListInput)
    const gridApiRef = useRef<GridApi | null>(null)
    const themeStyle = useAppSelector(selectThemeStyle)
    const theme = useAppSelector(selectTheme)
    const isMobile = useIsMobile(768)
    const [activeFilterColumn, setActiveFilterColumn] = useState<string>('')
    const [activeFilterText, setActiveFilterText] = useState<string>('')
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)
    const { t } = useTranslation()
    const userInfo = useAppSelector(selectUser)

    const textFilterParams = {
      filterOptions: ['contains'],
      debounceMs: 300,
      buttons: ['reset', 'apply'],
      maxNumConditions: 1,
      closeOnApply: true,
    }

    const columnDefs = useMemo<ColDef[]>(
      () => {
        const endDate = dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD')
        const isQuarterEnd = endDate === getCurrentQuarterRange()[1]
        const isFutureOrToday =
          dayjs(endDate).isSame(dayjs(), 'day') ||
          dayjs(endDate).isAfter(dayjs(), 'day')
        const isHistorical = !isQuarterEnd && !isFutureOrToday

        const baseColumns: ColDef[] = [
          {
            headerName: '',
            field: 'id',
            cellRenderer: (props: CustomCellRendererProps) => {
              if (props.value !== undefined) {
                return (
                  <IdeaActionColumn
                    data={props.data}
                    onEditIdea={onEditIdea}
                    onViewIdea={onViewIdea}
                  />
                )
              }
              return <></>
            },
            cellRendererParams: {
              onEditClick: onEditIdea,
              onHistoryClick: onViewIdea,
            },
            maxWidth: 110,
            pinned: isMobile ? null : 'left',
          },
          {
            headerName: t('idea_list.idea_table.columns.ticker_code'),
            field: 'tickerCode',
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
            pinned: isMobile ? null : 'left',
          },
          {
            headerName: t('idea_list.idea_table.columns.ticker_name'),
            field: 'tickerName',
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
          },
          {
            headerName: t('idea_list.idea_table.columns.ticker_legal_name'),
            field: 'tickerLegalName',
          },
          {
            headerName: t('idea_list.idea_table.columns.direction'),
            field: 'direction',
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
            cellRenderer: (props: CustomCellRendererProps) => {
              if (props.value !== undefined) {
                return <IdeaDirectionTag direction={props.data.direction} />
              }
              return <></>
            },
          },
          {
            headerName: t('idea_list.idea_table.columns.daily_pnl'),
            field: 'dailyPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('idea_list.idea_table.columns.quarter_pnl'),
            field: 'quarterPnl',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              isHistorical
                ? '-'
                : currencyValueFormatter(params, {
                    decimal: 0,
                    currency: 'USD',
                  }),
            cellStyle: isHistorical ? undefined : coloredNumberCellStyle,
          },
          {
            headerName: t('idea_list.idea_table.columns.total_return_pct'),
            field: 'totalReturnPct',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              isHistorical ? '-' : percentageValueFormatter(params),
            cellStyle: isHistorical ? undefined : coloredNumberCellStyle,
          },
          {
            headerName: t('idea_list.idea_table.columns.current_total_pnl'),
            field: 'currentTotalPnl',
            type: 'numberColumn',
            cellStyle: coloredNumberCellStyle,
            cellRenderer: IdeaTotalPnlCell,
            cellRendererParams: {
              onButtonClick: (rowData: IdeaListItem) => {
                onViewHistoryIdea(rowData.id)
              },
            },
          },
          {
            headerName: t('idea_list.idea_table.columns.avg_cost'),
            field: 'avgCost',
            type: 'numberColumn',
            valueFormatter: currencyValueFormatter,
          },
          {
            headerName: t('idea_list.idea_table.columns.current_market_value'),
            field: 'currentMarketValue',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              currencyValueFormatter(params, { decimal: 0, currency: 'USD' }),
          },
          {
            headerName: t('idea_list.idea_table.columns.pct_gmv'),
            field: 'pctGMV',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              isHistorical ? '-' : percentageValueFormatter(params),
            cellStyle: isHistorical ? undefined : coloredNumberCellStyle,
          },
          {
            headerName: t('idea_list.idea_table.columns.pct_bp'),
            field: 'pctBP',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              isHistorical ? '-' : percentageValueFormatter(params),
            cellStyle: isHistorical ? undefined : coloredNumberCellStyle,
          },
          {
            headerName: t('idea_list.idea_table.columns.shares'),
            field: 'shares',
            type: 'numberColumn',
          },
          {
            headerName: t('idea_list.idea_table.columns.target_market_value'),
            field: 'targetMarketValue',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              isHistorical
                ? '-'
                : currencyValueFormatter(params, {
                    decimal: 0,
                    currency: 'USD',
                  }),
          },
          {
            headerName: t('idea_list.idea_table.columns.contributor'),
            field: 'contributor',
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
          },
          {
            headerName: t('idea_list.idea_table.columns.firm_name'),
            field: 'firmName',
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
          },
          {
            headerName: t('idea_list.idea_table.columns.state'),
            field: 'state',
            filter: 'agTextColumnFilter',
            filterParams: textFilterParams,
          },
          {
            headerName: t('idea_list.idea_table.columns.duration'),
            field: 'duration',
            type: 'numberColumn',
            valueFormatter: (params: ValueFormatterParams) =>
              isHistorical ? '-' : params.value,
          },
          {
            headerName: t('idea_list.idea_table.columns.created_at'),
            field: 'createdAt',
            type: ['dateColumn'],
            valueFormatter: (params: ValueFormatterParams) =>
              marketDateTimezoneFormatter(params, { showTimezone: true }),
          },
          {
            headerName: t('idea_list.idea_table.columns.closed_at'),
            field: 'closedAt',
            type: ['dateColumn'],
            valueFormatter: (params: ValueFormatterParams) =>
              marketDateTimezoneFormatter(params, { showTimezone: true }),
          },
        ]

        // Only show account type for internal admin users
        if (
          userInfo.role === 'Admin' ||
          userInfo.role === 'Master' ||
          userInfo.role === 'SuperViewer'
        ) {
          baseColumns.push({
            headerName: t('idea_list.idea_table.columns.account_type'),
            field: 'isTrial',
            cellRenderer: IsTrialCellRenderer,
          })
        }

        const savedState = localStorage.getItem(IDEA_LIST_COLUMN_STATE_KEY)
        if (savedState) {
          try {
            const state = JSON.parse(savedState)
            const colOrder = state.map((col: { colId: string }) => col.colId)
            const ordered = colOrder
              .map((id: string) => baseColumns.find((col) => col.field === id))
              .filter(Boolean)
            const missing = baseColumns.filter(
              (col) => !colOrder.includes(col.field)
            )
            return [...ordered, ...missing]
          } catch {
            return baseColumns
          }
        }

        return baseColumns
      },

       
      [
        userInfo,
        ideaListInput,
        onEditIdea,
        onViewIdea,
        onViewHistoryIdea,
        isMobile,
        t,
      ]
    )

    const defaultColDef = useMemo<ColDef>(
      () => ({
        flex: 1,
        minWidth: 200,
        sortable: true,
        resizable: true,
        filter: false,
      }),
      []
    )

    const pageSize = 20

    const baseQuery = getBaseQuery(getBaseUrl('idea'))

    const convertSortModel = (sortModel: SortModelItem[]) => {
      if (sortModel.length > 0) {
        const orderBy = sortMapping[sortModel[0].colId]
        const order = sortModel[0].sort.toUpperCase()

        return [orderBy, order]
      }
      return null
    }

    const datasource = useMemo(
      () => ({
        getRows: async (params: IGetRowsParams) => {
          // Show ag-grid's built-in loading overlay only on first page and initial load
          const isFirstPage = params.startRow === 0
          if (isFirstPage && isInitialLoad) {
            gridApiRef.current?.showLoadingOverlay()
          }

          const [region, netCategory] = ideaListInput.category
            ? ideaListInput.category.split('-').map((s) => s.trim())
            : ['', '']

          try {
            const sortResult = convertSortModel(params.sortModel)
            const orderBy = sortResult ? sortResult[0] : ''
            const order = sortResult ? sortResult[1] : ''

            const filterColumn = activeFilterColumn
              ? sortMapping[activeFilterColumn] || activeFilterColumn
              : ''
            const filterText = activeFilterText || ''

            const userId = ideaListInput.onlyMyIdea
              ? userInfo.sub
              : ideaListInput.contributor || ''

            const rawStartDate = ideaListInput.dateRange[0]
            const rawEndDate = ideaListInput.dateRange[1]

            const startDate = dayjs(rawStartDate).isValid()
              ? dayjs(rawStartDate).format('YYYY-MM-DD')
              : undefined

            const endDate = dayjs(rawEndDate).isValid()
              ? dayjs(rawEndDate).format('YYYY-MM-DD')
              : undefined

            const request: IdeaListRequest = {
              page: Math.floor(params.startRow / pageSize) + 1,
              pageSize: pageSize,
              includeLiquidated: ideaListInput.showLiquidatedIdeas,
              firmId: ideaListInput.firm || '',
              userId: userId,
              region: region || '',
              netCategory: netCategory || '',
              orderBy: orderBy,
              orderDirection: order,
              startDate: startDate,
              endDate: endDate,
              filterColumn,
              filterText,
            }

            const result = await baseQuery(
              {
                url: buildIdeaListUrl(request),
                method: 'GET',
              },
              {} as BaseQueryApi,
              {}
            )

            const dataResult = result as { data?: IdeaListResponse }

            if (dataResult && dataResult.data && dataResult.data.data) {
              const ideas = dataResult.data.data.ideas
              const lastRow =
                ideas.length < pageSize
                  ? params.startRow + ideas.length
                  : undefined

              gridApiRef.current?.hideOverlay()
              params.successCallback(ideas, lastRow)

              // Only hide loading if we showed it AND reset the flag
              if (isFirstPage && isInitialLoad) {
                setIsInitialLoad(false)
              }
            } else {
              params.failCallback()
              if (isFirstPage && isInitialLoad) {
                setIsInitialLoad(false)
              }
            }

            gridApiRef.current?.hideOverlay()
          } catch {
            params.failCallback()
            if (isFirstPage && isInitialLoad) {
              setIsInitialLoad(false)
            }
          }
        },
      }),
       
      [
        isInitialLoad,
        activeFilterColumn,
        activeFilterText,
        ideaListInput.showLiquidatedIdeas,
        ideaListInput.firm,
        ideaListInput.contributor,
        ideaListInput.category,
        ideaListInput.onlyMyIdea,
        ideaListInput.dateRange,
      ]
    )

    const handleGridReady = useCallback((params: GridReadyEvent) => {
      gridApiRef.current = params.api
      const savedState = localStorage.getItem(IDEA_LIST_COLUMN_STATE_KEY)
      if (savedState) {
        const state = JSON.parse(savedState)
        gridApiRef.current.applyColumnState({
          state: state,
          applyOrder: true,
        })
      }
    }, [])

    const handleColumnChanged = useCallback(() => {
      if (!gridApiRef.current) return
      const state = gridApiRef.current.getColumnState()
      localStorage.setItem(IDEA_LIST_COLUMN_STATE_KEY, JSON.stringify(state))
    }, [])

    const handleSortChanged = useCallback(() => {
      if (!gridApiRef.current) return
      const state = gridApiRef.current.getColumnState()
      localStorage.setItem(IDEA_LIST_COLUMN_STATE_KEY, JSON.stringify(state))
    }, [])

    const handleFilterChanged = useCallback(
      (event: FilterChangedEvent) => {
        if (!event.api) return

        const filterModel = event.api.getFilterModel()
        const filterColumns = Object.keys(filterModel)

        if (filterColumns.length === 0) {
          setActiveFilterColumn('')
          setActiveFilterText('')
          return
        }

        if (filterColumns.length > 1) {
          const newFilter = filterColumns.find(
            (col) => col !== activeFilterColumn
          )

          if (newFilter) {
            const newFilterModel = { [newFilter]: filterModel[newFilter] }
            event.api.setFilterModel(newFilterModel)

            setActiveFilterColumn(newFilter)
            setActiveFilterText(filterModel[newFilter].filter || '')
            return
          }
        }

        if (filterColumns.length === 1) {
          const singleFilterColumn = filterColumns[0]
          setActiveFilterColumn(singleFilterColumn)
          setActiveFilterText(filterModel[singleFilterColumn].filter || '')
        }

        // Get the most recently changed column
        const changedColumn = filterColumns[filterColumns.length - 1]

        if (changedColumn && filterModel[changedColumn]) {
          if (changedColumn !== activeFilterColumn && activeFilterColumn) {
            const newFilterModel = {
              [changedColumn]: filterModel[changedColumn],
            }
            event.api.setFilterModel(newFilterModel)
          }

          setActiveFilterColumn(changedColumn)
          setActiveFilterText(filterModel[changedColumn].filter || '')
          return
        }
      },
      [activeFilterColumn]
    )

    // Expose refresh method to parent
    useImperativeHandle(ref, () => ({
      refresh: () => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshInfiniteCache()
          onRefreshed?.()
        }
      },
    }))

    // Refresh when filters change
    useEffect(() => {
      if (gridApiRef.current) {
        onRefreshed?.()
      }
    }, [
      ideaListInput.category,
      ideaListInput.firm,
      ideaListInput.contributor,
      ideaListInput.dateRange,
      ideaListInput.onlyMyIdea,
      ideaListInput.showLiquidatedIdeas,
      ideaListInput.onlyPremiumLeague,
      onRefreshed,
    ])

    // Periodically refresh all currently loaded data every 30s
    useEffect(() => {
      const interval = setInterval(() => {
        if (gridApiRef.current) {
          gridApiRef.current.refreshInfiniteCache()
          onRefreshed?.()
        }
      }, 30000)
      return () => clearInterval(interval)
    }, [onRefreshed])

    const getContextMenuItems = (params: GetContextMenuItemsParams) => {
      const items: MenuItemDef[] = []

      // Only show Edit if canEdit is true
      if (params.node && params.node.data && params.node.data.canEdit) {
        items.push({
          name: t('idea_list.idea_table.edit'),
          action: () => {
            onEditIdea(params.node?.data.id)
          },
          icon: ReactDOMServer.renderToString(
            <EditOutlined style={{ color: themeStyle.colorText }} />
          ),
        })
      }

      items.push({
        name: t('idea_list.idea_table.view'),
        action: () => {
          if (params.node) {
            onViewIdea(params.node.data.id)
          }
        },
        icon: ReactDOMServer.renderToString(
          <EyeOutlined style={{ color: themeStyle.colorText }} />
        ),
      })

      if (params.node && params.node.data && params.node.data.canEdit) {
        items.push({
          name: t('idea_list.idea_table.liquidate'),
          action: () => {
            if (params.node) {
              onLiquidateIdea(params.node.data.id)
            }
          },
          icon: ReactDOMServer.renderToString(
            theme === Theme.Dark ? <LiquidateDarkIcon /> : <LiquidateIcon />
          ),
        })
      }

      return items
    }

    return (
      <IdeyaAgGridTable
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowModelType={'infinite'}
        datasource={datasource}
        cacheBlockSize={pageSize}
        onGridReady={handleGridReady}
        onColumnMoved={handleColumnChanged}
        onColumnPinned={handleColumnChanged}
        onColumnVisible={handleColumnChanged}
        onSortChanged={handleSortChanged}
        getContextMenuItems={getContextMenuItems}
        onFilterChanged={handleFilterChanged}
        height={height}
      />
    )
  }
)
